import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { gov } from "@/lib/governanceApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { card, Pill, SectionTitle, Act, Field, errMsg } from "./ui";

const CONFIG = {
  suppliers: { title: "Sub-processor & integration register", sub: "Suppliers discovered in the code. Subprocessors, public data sources and unused integrations are recorded separately. 'Unknown — verify' means not yet evidenced.", main: "name", cols: ["role", "region", "dd_status"] },
  incidents: { title: "Security & incident register", sub: "Incidents, vulnerabilities and compliance gaps with remediation and notification tracking. Internal only — never shown to customers.", main: "title", cols: ["type", "severity", "status"] },
  controls: { title: "Compliance evidence register", sub: "Each requirement with its source, classification, control, evidence and status. A file existing is not evidence of compliance.", main: "requirement", cols: ["classification", "priority", "control_status"] },
};

const Editor = ({ kind, fields, rec, onClose, onDone }) => {
  const [f, setF] = useState(rec || {});
  const save = async () => {
    try {
      if (rec?.id) await gov.saveRecord(kind, rec.id, f); else await gov.createRecord(kind, f);
      toast.success("Saved"); onDone(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto" data-testid={`${kind}-editor`}>
        <DialogHeader><DialogTitle>{rec?.id ? "Edit record" : "New record"}</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-2 gap-3">
          {Object.entries(fields).map(([k, spec]) => (
            <div key={k} className={(spec.max || 0) > 300 ? "md:col-span-2" : ""}>
              <Field name={k} spec={spec} value={f[k]} testid={`${kind}-field-${k}`} onChange={(n, v) => setF({ ...f, [n]: v })} />
            </div>
          ))}
        </div>
        <Act onClick={save} data-testid={`${kind}-save`}>Save (audited)</Act>
      </DialogContent>
    </Dialog>
  );
};

export default function Register({ kind }) {
  const qc = useQueryClient();
  const cfg = CONFIG[kind];
  const { data: meta } = useQuery({ queryKey: ["gov-meta"], queryFn: gov.meta });
  const { data = [] } = useQuery({ queryKey: ["gov-reg", kind], queryFn: () => gov.register(kind) });
  const [edit, setEdit] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const rows = data.filter((r) => showArchived || r.status !== "archived");
  return (
    <div>
      <div className="flex justify-between items-start gap-4">
        <SectionTitle sub={cfg.sub}>{cfg.title}</SectionTitle>
        <Act onClick={() => setEdit({})} data-testid={`${kind}-new`}><Plus size={13} className="mr-1" /> Add</Act>
      </div>
      <label className="text-xs flex items-center gap-2 mb-2"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived</label>
      <div className={`${card} divide-y divide-zinc-100`} data-testid={`${kind}-table`}>
        {rows.map((r) => (
          <button key={r.id} onClick={() => setEdit(r)} data-testid={`${kind}-row-${r.id}`} className="w-full text-left px-4 py-3 hover:bg-zinc-50 flex flex-wrap items-center gap-3 justify-between">
            <span className="text-sm font-bold flex-1 min-w-[240px]">{r[cfg.main]}</span>
            <span className="flex flex-wrap gap-1">{cfg.cols.map((c) => <Pill key={c} value={r[c]} />)}</span>
          </button>
        ))}
        {rows.length === 0 && <div className="p-4 text-sm text-zinc-400">No records</div>}
      </div>
      {edit && meta && <Editor kind={kind} fields={meta.registers[kind]} rec={edit} onClose={() => setEdit(null)} onDone={() => qc.invalidateQueries({ queryKey: ["gov-reg", kind] })} />}
    </div>
  );
}
